import { Component, HostBinding, Input, OnDestroy, OnInit, EventEmitter, Output } from '@angular/core'
import { AuthenticationService } from '../../../../../framework/authentication/services/authentication.service'
import { AbstractControl, FormControl, Validators } from '@angular/forms'
import { Observable, Subscription } from 'rxjs'
import { DealContractQuery } from 'generated/graphql'
import { LesseeCompany } from 'modules/deals/utilities/deal-types'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'

@UntilDestroy()
@Component({
    selector: 'mg-deal-signing-persons',
    templateUrl: './deal-signing-persons.component.html',
    styleUrls: ['./deal-signing-persons.component.scss'],
})
export class DealSigningPersonsComponent implements OnInit, OnDestroy {
    value: string[]
    userIsSigningPersonSubscription: Subscription
    userIsSigningPersonControl: AbstractControl = new FormControl({
        value: undefined,
        disabled: this.disabled,
    })
    extraSigningPersons: {
        control: AbstractControl
        subscription: Subscription
    }[] = []
    chamberOfCommerceNumber: string
    companyName: string

    @Input()
    deal$: Observable<DealContractQuery['Deal']>

    @Output()
    signingPersonChange: EventEmitter<string[]> = new EventEmitter()

    constructor(private authenticationService: AuthenticationService) {}

    @Input()
    get userIsSigningPerson() {
        return this.userIsSigningPersonControl.value
    }

    set userIsSigningPerson(value) {
        this.userIsSigningPersonControl.setValue(value)
    }

    private _disabled: boolean = false

    @Input()
    @HostBinding('class._is_disabled')
    get disabled(): boolean {
        return this._disabled
    }

    set disabled(value) {
        const action = value ? 'disable' : 'enable'
        this._disabled = value
        this.userIsSigningPersonControl[action]()
        this.extraSigningPersons.forEach(({ control }) => control[action]())
    }

    setDisabledState(disabled: boolean) {
        this.disabled = disabled
    }

    async ngOnInit() {
        this.deal$.pipe(untilDestroyed(this)).subscribe(deal => {
            if (deal && deal.lessee) {
                this.chamberOfCommerceNumber = (deal.lessee as LesseeCompany).chamberOfCommerceNr
                this.companyName = (deal.lessee as LesseeCompany).name
            }
        })

        this.userIsSigningPersonSubscription = this.userIsSigningPersonControl.valueChanges.subscribe(
            userIsSigningPerson => {
                if (!userIsSigningPerson && !this.extraSigningPersons.length) {
                    this.addExtraSigningPerson()
                } else if (userIsSigningPerson && this.extraSigningPersons.length) {
                    while (this.extraSigningPersons.some(({ control }) => !control.value)) {
                        this.removeExtraSigningPerson(
                            this.extraSigningPersons.findIndex(({ control }) => !control.value)
                        )
                    }
                }

                this.updateValue()
            }
        )
    }

    ngOnDestroy() {
        this.userIsSigningPersonSubscription.unsubscribe()
        this.extraSigningPersons.forEach(({ subscription }) => subscription.unsubscribe())
    }

    updateValue() {
        const value = this.extraSigningPersons.map(({ control: { value } }) => value)

        if (this.userIsSigningPersonControl.value) {
            value.unshift(this.authenticationService.currentUser.getValue().email)
        }

        this.value = value.filter(v => v)
        this.signingPersonChange.emit(this.value)
    }

    addExtraSigningPerson() {
        const control = new FormControl('', [Validators.required])
        const subscription = control.valueChanges.subscribe(this.updateValue.bind(this))

        this.extraSigningPersons.push({ control, subscription })

        this.updateValue()
    }

    removeExtraSigningPerson(index) {
        const [extraSingingPerson] = this.extraSigningPersons.splice(index, 1)

        extraSingingPerson.subscription.unsubscribe()

        this.updateValue()
    }

    async validate(): Promise<null | any> {
        let result = null

        if (this.value.filter(i => i.length).length === 0) {
            result = { length: 'add at least one signingperson' }
        } else if (this.extraSigningPersons.filter(({ control }) => control.value.length && control.invalid).length) {
            result = { email: 'one or more invalid email adresses' }
        }

        return result
    }

    get containsDuplicateEmails() {
        return this.value.some((item, index) => this.value.lastIndexOf(item) > index)
    }

    get duplicateEmail() {
        return this.value.filter((item, index) => this.value.lastIndexOf(item) > index)[0]
    }
}
